<template>
  <NTable
    v-show="viewMode?.value === 0 || viewMode?.value === 1"
    :key="viewMode?.value"
    ref="tableRef"
    :data="data"
    :column-splitter="false"
    :striped="false"
    cell-padding="small"
    :sort-by="sortBy"
    :highlight-by="{
      prop: 'isin',
      value: activeIsin,
    }"
    fixed-header
    :class="[
      'scrollbar max-h-full !h-auto w-full',
      {
        'both max-w-[calc(50%_-_3rem)]': viewMode?.value === 0,
        'call w-full max-w-full': viewMode?.value === 1,
      },
    ]"
    hover-alignment="right"
    :hover-offset="viewMode?.value === 0 ? '10rem' : '16rem'"
    @click.stop
    identifier="isin"
    @row-click="(row) => updateActiveIsin(row.isin)"
    @scroll="onScrollHandler"
  >
    <template #hoveredElements="{ row }">
      <div class="flex items-center h-full gap-2">
        <NButton
          v-gid="{
            module: 'option',
            screen: 'home',
            section: 'optionChain',
            component: 'buyButton',
          }"
          size="mini"
          variant="fill"
          @click.stop="openOrderDialog(row, ORDER_TYPES.buy)"
        >
          <NText variant="overline" weight="medium">
            {{ t('common.buy') }}
          </NText>
        </NButton>

        <NButton
          v-gid="{
            module: 'option',
            screen: 'home',
            section: 'optionChain',
            component: 'sellButton',
          }"
          size="mini"
          color="red"
          variant="fill"
          @click.stop="openOrderDialog(row, ORDER_TYPES.sell)"
        >
          <NText variant="overline" weight="medium">
            {{ t('common.sell') }}
          </NText>
        </NButton>
      </div>
    </template>

    <NTableColumn
      v-slot="{ row }"
      v-gid="{
        module: 'symbol',
        screen: 'optionChaniTable',
        section: 'mainTable',
        component: 'mainTable',
      }"
      :label="t('common.symbol')"
      prop="instrumentName"
      class="w-[8rem]"
      align="text-right"
    >
      <span
        :key="row.isin"
        class="inline-flex items-center min-w-[6rem] whitespace-nowrap"
      >
        <SymbolTradeStateIcon
          class="me-1"
          :trading-state-code="row.instrumentTradingState"
        />
        <NText variant="caption2" weight="medium">
          {{ row.instrumentName }}
        </NText>
      </span>
    </NTableColumn>

    <NTableColumn
      v-if="viewMode?.value === 1"
      v-slot="{ row }"
      :label="t('option.strikePrice')"
      prop="strikePrice"
      class="w-[2rem] p-[0.58rem]"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.strikePrice" round>
        <NText weight="medium" variant="caption2" dir="ltr">
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('symbol.last_trade_price')"
      prop="referencePriceVariationPercentage"
      align="text-right"
    >
      <div
        :key="`${row.isin}-${row.lastTradePrice}-${row.referencePriceVariationPercentage}`"
        class="flex items-center whitespace-nowrap gap-1"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.referencePriceVariationPercentage / 100"
          number-style="percent"
          maximum-fraction-digits="2"
        >
          <ChangePriceEffect :price="row.referencePriceVariationPercentage">
            <NText
              variant="caption2"
              weight="medium"
              :class="{
                'text-brand-primary': row.referencePriceVariationPercentage > 0,
                'text-critical-primary':
                  row.referencePriceVariationPercentage < 0,
              }"
              dir="ltr"
            >
              ({{ number }})
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>

        <NumberFormatter #="{ number }" :value="row.lastTradePrice">
          <ChangePriceEffect :price="row.lastTradePrice">
            <NText
              :key="row.lastTradePrice"
              variant="caption2"
              weight="medium"
              dir="ltr"
            >
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>
      </div>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.order.closingPrice')"
      prop="closingPriceVariationPercentage"
      align="text-right"
    >
      <div
        :key="`${row.isin}-${row.closingPrice}-${row.closingPriceVariationPercentage}`"
        class="flex items-center whitespace-nowrap gap-1"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.closingPriceVariationPercentage / 100"
          number-style="percent"
          maximum-fraction-digits="2"
        >
          <ChangePriceEffect :price="row.closingPriceVariationPercentage">
            <NText
              variant="caption2"
              weight="medium"
              :class="{
                'text-brand-primary': row.closingPriceVariationPercentage > 0,
                'text-critical-primary':
                  row.closingPriceVariationPercentage < 0,
              }"
              dir="ltr"
            >
              ({{ number }})
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>

        <NumberFormatter #="{ number }" :value="row.closingPrice">
          <ChangePriceEffect :price="row.closingPrice">
            <NText
              :key="row.closingPrice"
              variant="caption2"
              weight="medium"
              dir="ltr"
            >
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>
      </div>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('symbol.trade_value')"
      prop="totalTradeValue"
      align="text-right"
    >
      <NumberFormatter
        #="{ number }"
        notation="compact"
        maximum-fraction-digits="2"
        :value="row.totalTradeValue"
      >
        <NText
          class="whitespace-nowrap"
          weight="medium"
          variant="caption2"
          dir="ltr"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('symbol.trade_volume')"
      prop="totalNumberOfSharesTraded"
      align="text-right"
    >
      <NumberFormatter
        #="{ number }"
        :value="row.totalNumberOfSharesTraded"
        maximum-fraction-digits="2"
      >
        <NText
          class="whitespace-nowrap"
          variant="caption1"
          weight="medium"
          dir="ltr"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('option.openPosition')"
      prop="marketOpenPositions"
      align="text-right"
    >
      <NumberFormatter
        #="{ number }"
        :value="row.marketOpenPositions"
        maximum-fraction-digits="2"
      >
        <NText
          class="whitespace-nowrap"
          variant="caption1"
          weight="medium"
          dit="ltr"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.break_point')"
      prop="breakPointPercentage"
      align="text-right"
    >
      <div
        :key="`${row.isin}-${row.breakPoint}-${row.breakPointPercentage}`"
        class="flex items-center whitespace-nowrap gap-1"
      >
        <NumberFormatter
          #="{ number }"
          :value="row.breakPointPercentage / 100"
          number-style="percent"
          maximum-fraction-digits="2"
        >
          <ChangePriceEffect :price="row.breakPointPercentage / 100">
            <NText
              variant="caption2"
              weight="medium"
              :class="{
                'text-brand-primary': row.breakPointPercentage > 0,
                'text-critical-primary': row.breakPointPercentage < 0,
              }"
              class="whitespace-nowrap"
              dir="ltr"
            >
              ({{ number }})
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>

        <NumberFormatter #="{ number }" :value="row.breakPoint">
          <ChangePriceEffect :price="row.breakPoint">
            <NText
              :key="row.breakPoint"
              variant="caption2"
              weight="medium"
              dir="ltr"
            >
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>
      </div>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('common.state')"
      prop="status"
      align="text-right"
    >
      <NText
        :key="row.status"
        variant="caption2"
        weight="medium"
        class="rounded-md px-2 py-0.5 whitespace-nowrap"
        :class="
          row.status > 0
            ? 'bg-brand-secondary text-brand-primary'
            : 'bg-critical-secondary text-critical-primary'
        "
      >
        {{ useGetOptionContractStateChainLabel(row.status) }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('option.blackScholes')"
      prop="blackScholes"
      align="text-right"
    >
      <NumberFormatter
        #="{ number }"
        round
        maximum-fraction-digits="2"
        :value="row.blackScholes"
      >
        <NText
          class="whitespace-nowrap"
          weight="medium"
          variant="caption2"
          dir="ltr"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('option.delta')"
      prop="delta"
      align="text-right"
    >
      <NumberFormatter
        #="{ number }"
        maximum-fraction-digits="2"
        :value="row.delta"
      >
        <NText
          class="whitespace-nowrap"
          weight="medium"
          variant="caption2"
          dir="ltr"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('option.lever')"
      prop="leverage"
      align="text-right"
    >
      <NumberFormatter
        #="{ number }"
        maximum-fraction-digits="2"
        :value="row.leverage"
      >
        <NText
          class="whitespace-nowrap"
          weight="medium"
          variant="caption2"
          dir="ltr"
        >
          {{ number }}
        </NText>
      </NumberFormatter>
    </NTableColumn>
  </NTable>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import { ORDER_TYPES } from '@core/order';
import { PositionItemWithPrices } from '@core/option';
import { OptionContractChainDetails } from '@libs/api-reference';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import { useOrderDialogs } from '@/modules/order';
import { useGetOptionContractStateChainLabel } from '@/modules/option';
import { SymbolTradeStateIcon, useActiveSymbolIsin } from '@/modules/symbol';

import { TViewMode } from './index.vue';

interface TProps {
  data: OptionContractChainDetails[];
  viewMode: TViewMode;
  sortBy?: unknown;
}

type TScroll = {
  top: number;
  left: number;
};

type TEmits = {
  (e: 'scroll', value: TScroll): void;
};

defineProps<TProps>();
const emit = defineEmits<TEmits>();

const tableRef = useTemplateRef('tableRef');

const { t } = useI18n();
const { openCreateOptionOrderDialog } = useOrderDialogs();
const { activeIsin, updateActiveIsin } = useActiveSymbolIsin();

const openOrderDialog = (row: PositionItemWithPrices, type: 'buy' | 'sell') => {
  openCreateOptionOrderDialog({
    type,
    symbol: {
      isin: row.isin,
      instrumentName: row.instrumentName,
    },
  });
};

function onScrollHandler(event: Event) {
  emit('scroll', {
    top: (event.target as Element).scrollTop,
    left: -(event.target as Element).scrollLeft,
  });
}

defineExpose({ tableRef });
</script>

<style lang="css" scoped>
.scrollbar {
  border: none;
  border-radius: unset;
}

.scrollbar :deep(tbody > tr:last-child) {
  @apply border-b border-neutral-secondary;
}

.both td:nth-child(1),
.call td:nth-child(1),
.call td:nth-child(2),
.call :deep(tr:nth-child(1) th:nth-child(1)),
.call :deep(tr:nth-child(1) th:nth-child(2)),
.both :deep(tr:nth-child(1) th:nth-child(1)) {
  @apply sticky right-0 bg-neutral-primary z-[45];
}

:deep(th:nth-child(1)) {
  @apply top-0 !z-60;
}

.call td:nth-child(2),
.call :deep(tr:nth-child(1) th:nth-child(2)) {
  @apply right-28;
}

.both td:nth-child(1)::after,
.both :deep(tr:nth-child(1) th:nth-child(1)::after) {
  @apply border-0 border-l border-neutral-secondary;
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 100%;
  background: rgba(0, 0, 0, 0.05);
  box-shadow: -2px 0px 6px 0px rgba(0, 0, 0, 0.2);
}

.call td:nth-child(1)::after,
.call td:nth-child(2)::after,
.call :deep(tr:nth-child(1) th:nth-child(1)::after),
.call :deep(tr:nth-child(1) th:nth-child(2)::after) {
  @apply border-0 border-l border-neutral-secondary;
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 100%;
  background: rgba(0, 0, 0, 0.05);
  box-shadow: -2px 0px 6px 0px rgba(0, 0, 0, 0.2);
}
</style>
